// CrystalClear Vaults — Network + Contract Configuration
//
// Single source of truth. Both mainnet and testnet are defined here.
// User picks one via the network toggle; choice persists in localStorage.
// Switching networks reloads the page (cleaner than re-binding everything).

// ── Stealth / launch mode ──────────────────────────────────────────────────
// When stealth=true:
//   - Deposits disabled across all pages (CTAs become "Opens <launch_date>")
//   - Performance metrics (Sharpe/Win/MDD/TVL/Share Price/P&L) hidden
//   - Trade list stays visible; trade P&L column hidden
//   - On wallet connect, POST to /api/early-visitor registers the address
//     and returns a position number ("You're #N"); powers the Share-on-X CTA
// To open to public: set stealth=false, `cd vault/frontend/build && npm run build`, redeploy.
// Full runbook: docs/fleet/STEALTH_MODE.md
export const APP_MODE = {
  // Flipped from true → false on 2026-05-19 launch. Public mode now: every
  // page renders the live UI for all visitors, deposit CTAs enabled, perf
  // metrics visible. The deposit_whitelist below is no longer load-bearing
  // (whitelist bypass only fires when stealth=true) — kept for reference.
  // To re-enter stealth (e.g. to gate a campaign), flip back to true and
  // rebuild bundles. Full runbook: docs/fleet/STEALTH_MODE.md
  stealth: false,
  launch_date: "2026-05-19T12:00:00Z",  // historical; ignored when stealth=false
  x_handle: "@CrystalClearHL",
  // Pre-launch tester whitelist. Wallets listed here bypass stealth: they see
  // the full live UI AND can deposit (CTA stays enabled). Existing depositors
  // who aren't on the list still see the live dashboard but their deposit CTA
  // stays locked until launch_date. Lowercase 0x addresses, exact match.
  deposit_whitelist: [
    "0x0984fcb56c4170d8cdd6c6470d10b1eb8ebd7891",  // tester #1 (added 2026-05-09)
    "0x1b956463f6cae00c942344ea05cc1699ca43ef5d",  // tester #2 — JP's friend (added 2026-05-12)
    "0x802215ab7f0cb231e4fff7324dbbf095c87cb4ee",  // tester #3 (added 2026-05-12)
  ],
};

// ── Vault gemstone identities (shared across networks) ─────────────────────
// Locked fleet of 10 per docs/fleet/DESIGN.md §4.1.
// Per-network `vaults` array references these by `key`, overrides address+status+launchSharePrice.
// Onyx is live on mainnet (legacy name "CrystalVault"/"cvUSDC" on-chain, display "cvONYX"/"Onyx").
// The 9 new gemstones use the CrystalClear naming: contract name "CrystalClear <X>", symbol "cc<X>".
// Metrics for the 9 new vaults populate once each goes live + has history.
export const GEMSTONES = {
  // Description rules (loadbearing):
  //   • NEVER hardcode strategy count ("20 strategies", "15 strategies") —
  //     rebalances drift the number every cycle. The detail page shows the
  //     current count live from the API.
  //   • DO state the selection criteria (what makes THIS vault unique vs.
  //     others). That's the static characteristic — it doesn't change with
  //     rebalances.
  //   • Always frame trading universe as "N coins, long and short" — the
  //     bidirectional nature is the key macro-independence signal.
  // The timeframes field is the CANDLE INTERVALS the algos trade on, not
  // hold time. Hold time varies per strategy + market regime; median across
  // the fleet is ~9h (see landing page). Display label always says "candles"
  // to disambiguate from hold time.
  onyx: {
    name: "Onyx", key: "onyx", symbol: "cvONYX",
    subtitle: "Diversified Systematic",  // 2026-05-21: stripped "Alpha" per persona.md vocab table (forbidden as noun)
    description: "Diversified across 14 crypto assets, long and short. Selection prioritises low cross-strategy correlation and high risk-adjusted return — the broadest universe in the fleet. Rebalanced every two weeks: the live strategy roster is replaced with a freshly evolved cohort, out-of-sample validated before going live.",
    icon: "hexagon",
    color: { hex: "#64748B", text: "text-[#64748B]", bg10: "bg-[#64748B]/10", bg20: "bg-[#64748B]/20", dot: "bg-[#64748B]", shadow: "shadow-[0_0_15px_rgba(100,116,139,0.4)]" },
    // targetApr: per-vault discrete number in the [41, 63]% range, ordered by
    // displayed Sharpe (best vault gets highest APR). Single number per vault
    // so rendering produces "~63%" / "~60%" / etc. Shown in stealth too (this
    // is the only performance number we advertise pre-launch; Sharpe/MDD/
    // WinRate stay dashed because their backtest/live divergence is known to
    // be material). Range tightened to 41-63 on 2026-04-28 to align all vaults
    // around a more aggressive but still bounded advertised yield.
    // Metrics policy (post-2026-05-15): winRate, maxDD, medianHoldHours
    // are EMPIRICAL — computed from live data since each vault's launch
    // (closed-trades parquet for winRate + hold time, share-price-history
    // JSONLs for maxDD). NOT from backtest. Update on each refresh script
    // run. Sharpe stays backtest-derived until live history is long enough
    // (>3 months) to produce a stable live Sharpe estimate.
    // TODO post-launch: swap to a fetched endpoint so these refresh
    // automatically. For now, manual refresh — last sync: 2026-05-15.
    metrics: { sharpe: 7.74, winRate: 24, rewardRisk: 6.3, medianHoldHours: 9.6, maxDD: 23.3, coins: 14, timeframes: "30m·1h·2h", targetApr: 63 },
  },
  amber: {
    name: "Amber", key: "amber", symbol: "ccAMBER",
    subtitle: "Momentum-Tilted",
    description: "Trades 11 coins, long and short. Selection emphasises momentum-style strategies validated on a recent in-sample window — picks up trend regimes faster than the patience-weighted vaults. Rebalanced every two weeks: the live strategy roster is replaced with a freshly evolved cohort, out-of-sample validated before going live.",
    icon: "grain",
    color: { hex: "#F59E0B", text: "text-[#F59E0B]", bg10: "bg-[#F59E0B]/10", bg20: "bg-[#F59E0B]/20", dot: "bg-[#F59E0B]", shadow: "shadow-[0_0_15px_rgba(245,158,11,0.4)]" },
    metrics: { sharpe: 7.44, winRate: 27, rewardRisk: 1.9, medianHoldHours: 18.5, maxDD: 15.5, coins: 11, timeframes: "30m·1h·2h", targetApr: 60 },
  },
  moonstone: {
    name: "Moonstone", key: "moonstone", symbol: "ccMOONSTONE",
    subtitle: "Selective Low-Turnover",
    description: "Trades 9 coins, long and short. Selection optimises for highest Sharpe at lower turnover — patient, selective entries. Fewer trades per year, higher quality per trade. Rebalanced every two weeks: the live strategy roster is replaced with a freshly evolved cohort, out-of-sample validated before going live.",
    icon: "nights_stay",
    color: { hex: "#C7D2FE", text: "text-[#C7D2FE]", bg10: "bg-[#C7D2FE]/10", bg20: "bg-[#C7D2FE]/20", dot: "bg-[#C7D2FE]", shadow: "shadow-[0_0_15px_rgba(199,210,254,0.4)]" },
    metrics: { sharpe: 3.64, winRate: 24, rewardRisk: 4.1, medianHoldHours: 9.6, maxDD: 10.6, coins: 9, timeframes: "30m·1h·2h", targetApr: 42 },
  },
  peridot: {
    name: "Peridot", key: "peridot", symbol: "ccPERIDOT",
    subtitle: "Precision Targeting",
    description: "Coming soon.",
    icon: "eco",
    color: { hex: "#84CC16", text: "text-[#84CC16]", bg10: "bg-[#84CC16]/10", bg20: "bg-[#84CC16]/20", dot: "bg-[#84CC16]", shadow: "shadow-[0_0_15px_rgba(132,204,22,0.4)]" },
    metrics: { sharpe: 5.90, winRate: null, rewardRisk: null, medianHoldHours: null, maxDD: null, coins: 10, timeframes: "30m·1h·2h", targetApr: 53 },
  },
  ruby: {
    name: "Ruby", key: "ruby", symbol: "ccRUBY",
    subtitle: "High-Conviction Concentrated",
    description: "Trades 11 coins, long and short. Selection weights higher-conviction signals — fewer simultaneous positions, larger per-trade allocation when entries fire. Rebalanced every two weeks: the live strategy roster is replaced with a freshly evolved cohort, out-of-sample validated before going live.",
    icon: "local_fire_department",
    color: { hex: "#DC2626", text: "text-[#DC2626]", bg10: "bg-[#DC2626]/10", bg20: "bg-[#DC2626]/20", dot: "bg-[#DC2626]", shadow: "shadow-[0_0_15px_rgba(220,38,38,0.4)]" },
    metrics: { sharpe: 5.54, winRate: 17, rewardRisk: 13.5, medianHoldHours: 8.5, maxDD: 14.9, coins: 11, timeframes: "30m·1h·2h", targetApr: 50 },
  },
  diamond: {
    name: "Diamond", key: "diamond", symbol: "ccDIAMOND",
    subtitle: "Concentrated Focused",
    description: "Trades 8 coins, long and short. The most concentrated universe in the fleet — fewer assets, tighter strategy selection. Designed for clarity over coverage. Rebalanced every two weeks: the live strategy roster is replaced with a freshly evolved cohort, out-of-sample validated before going live.",
    icon: "auto_awesome",
    color: { hex: "#E5E7EB", text: "text-[#E5E7EB]", bg10: "bg-[#E5E7EB]/10", bg20: "bg-[#E5E7EB]/20", dot: "bg-[#E5E7EB]", shadow: "shadow-[0_0_15px_rgba(229,231,235,0.4)]" },
    metrics: { sharpe: 2.80, winRate: 17, rewardRisk: 0.35, medianHoldHours: 4.5, maxDD: 22.1, coins: 8, timeframes: "30m·1h·2h", targetApr: 41 },
  },
  sapphire: {
    name: "Sapphire", key: "sapphire", symbol: "ccSAPPHIRE",
    subtitle: "Broad-Universe Diversified",
    description: "Coming soon.",
    icon: "water_drop",
    color: { hex: "#2563EB", text: "text-[#2563EB]", bg10: "bg-[#2563EB]/10", bg20: "bg-[#2563EB]/20", dot: "bg-[#2563EB]", shadow: "shadow-[0_0_15px_rgba(37,99,235,0.4)]" },
    metrics: { sharpe: 4.75, winRate: null, rewardRisk: null, medianHoldHours: null, maxDD: null, coins: 12, timeframes: "30m·1h·2h", targetApr: 47 },
  },
  emerald: {
    name: "Emerald", key: "emerald", symbol: "ccEMERALD",
    subtitle: "Resilient Growth",
    description: "Trades 12 coins, long and short. Selection optimises for resilient growth — historically the lowest drawdown in the fleet at a lower turnover than the momentum-heavy vaults. Rebalanced every two weeks: the live strategy roster is replaced with a freshly evolved cohort, out-of-sample validated before going live.",
    icon: "spa",
    color: { hex: "#10B981", text: "text-[#10B981]", bg10: "bg-[#10B981]/10", bg20: "bg-[#10B981]/20", dot: "bg-[#10B981]", shadow: "shadow-[0_0_15px_rgba(16,185,129,0.4)]" },
    metrics: { sharpe: 6.94, winRate: 33, rewardRisk: 3.75, medianHoldHours: 12.7, maxDD: 17.0, coins: 12, timeframes: "30m·1h·2h", targetApr: 58 },
  },
  pearl: {
    name: "Pearl", key: "pearl", symbol: "ccPEARL",
    subtitle: "Steady Compounding",
    description: "Coming soon.",
    icon: "blur_circular",
    color: { hex: "#FED7AA", text: "text-[#FED7AA]", bg10: "bg-[#FED7AA]/10", bg20: "bg-[#FED7AA]/20", dot: "bg-[#FED7AA]", shadow: "shadow-[0_0_15px_rgba(254,215,170,0.4)]" },
    metrics: { sharpe: 5.91, winRate: null, rewardRisk: null, medianHoldHours: null, maxDD: null, coins: 11, timeframes: "30m·1h·2h", targetApr: 55 },
  },
  opal: {
    name: "Opal", key: "opal", symbol: "ccOPAL",
    subtitle: "Balanced Multi-Factor",
    description: "Coming soon.",
    icon: "lens_blur",
    color: { hex: "#A78BFA", text: "text-[#A78BFA]", bg10: "bg-[#A78BFA]/10", bg20: "bg-[#A78BFA]/20", dot: "bg-[#A78BFA]", shadow: "shadow-[0_0_15px_rgba(167,139,250,0.4)]" },
    metrics: { sharpe: 3.82, winRate: null, rewardRisk: null, medianHoldHours: null, maxDD: null, coins: 10, timeframes: "30m·1h·2h", targetApr: 44 },
  },
};

// Rebalance history — manually populated for now; update on each rebalance
// event. Source of truth: `artifacts/evolution/vault_registry.jsonl` and the
// internal /api/dashboard/rebuilds endpoint. Move to a public API fetch
// once the user-facing crystalclear-api gets a /rebalances endpoint.
//
// Schema: list of {date: YYYY-MM-DD, n_strategies: int, summary: string}
// ordered NEWEST FIRST.
export const REBALANCE_HISTORY = {
  onyx: [
    { date: "2026-05-08", n_strategies: 10, summary: "Full fleet rebuild — 10 fresh strategies from GP cycle 8" },
    { date: "2026-04-20", n_strategies: 20, summary: "20-strategy rebuild from validated April pool" },
    { date: "2026-04-15", n_strategies: 20, summary: "First production rebalance — replaced launch strategies" },
  ],
  amber: [
    { date: "2026-05-08", n_strategies: 10, summary: "Full fleet rebuild — 10 fresh strategies from GP cycle 8" },
    { date: "2026-04-22", n_strategies: 20, summary: "Post-launch rebalance to expand strategy diversity" },
    { date: "2026-04-10", n_strategies: 50, summary: "Initial launch portfolio" },
  ],
  moonstone: [
    { date: "2026-05-08", n_strategies: 11, summary: "Full fleet rebuild — initial portfolio from GP cycle 8" },
  ],
  ruby: [
    { date: "2026-05-08", n_strategies: 10, summary: "Full fleet rebuild — initial portfolio from GP cycle 8" },
  ],
  diamond: [
    { date: "2026-05-08", n_strategies: 10, summary: "Full fleet rebuild — initial portfolio from GP cycle 8" },
  ],
  emerald: [
    { date: "2026-05-08", n_strategies: 11, summary: "Full fleet rebuild — initial portfolio from GP cycle 8" },
  ],
};

// ── Network registry ──────────────────────────────────────────────────────
export const NETWORKS = {
  mainnet: {
    key: "mainnet",
    label: "Mainnet",
    chainId: 999,
    chainHex: "0x3e7",
    chainName: "HyperEVM Mainnet",
    rpcUrl: "https://rpc.hyperliquid.xyz/evm",
    // hyperscan.com (Blockscout-based) is the canonical HyperEVM mainnet explorer.
    // Purrsec dropped EVM /address pages — its /address/<addr> now 404s.
    explorer: "https://www.hyperscan.com",
    // HL Core trade explorer — the vault contract address is ALSO an HL Core
    // user, and every trade the bot signs hits this address on HL. Depositors
    // can see live fills/positions/funding here that don't show up on
    // Hyperscan (which only sees EVM-side events like deposit/withdraw).
    // Pattern: /explorer/address/<vault_addr>. Derived from vc.address so
    // every future vault gets the link automatically — no per-vault config.
    hlExplorer: "https://app.hyperliquid.xyz/explorer",
    nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
    usdcAddress: "0xb88339CB7199b77E23DB6E890353E22632Ba630f",
    usdcDecimals: 6,
    // cvUSDC = USDC decimals (6) + virtual-shares offset (3) = 9.
    // OZ ERC4626Upgradeable.decimals() returns asset.decimals() + _decimalsOffset().
    // Was incorrectly 18 (assumed standard ERC20) — caused share displays to read 0.0000.
    shareDecimals: 9,
    isProduction: true,
    badgeText: "MAINNET",
    badgeColor: "emerald",
    vaults: [
      // Onyx — V3 deployed 2026-04-12, initialized 2026-04-13
      // launchSharePrice: the on-chain share price at the moment "performance tracking
      // should start" — i.e., AFTER the one-time HL activation fee is absorbed, BEFORE
      // any trading has happened. Display formula:
      //     displayed = on_chain_share_price / launchSharePrice
      // Today on-chain = $0.90 (activation fee took 1/10 of first deposit). launchSharePrice = 0.90.
      // After 30% trading profit: on_chain = $1.17 → displayed $1.17/$0.90 = $1.30 (+30%) ✓
      // Rationale: users care about trading performance, not the one-time platform fee.
      // launchDate = on-chain "live since" (registry activated_at, else deployed_at).
      // Drives the card's "Nd live" age (true contract age — NOT the APR window).
      // deposit_closed: legacy founding-fleet vaults are CLOSED to NEW deposits
      // during the Lagoon transition (deposits route to the new audited Lagoon
      // vaults once live). Existing depositors' positions + WITHDRAW are
      // untouched — this flag only hides the deposit CTA + shows a "closed to
      // new deposits — withdraw available" badge. See FRONTEND_TRANSITION_BRIEF
      // item 3. Flip back to undefined per-vault if a vault ever reopens.
      { ...GEMSTONES.onyx, address: "0x231f66c336512e897855420a2788B83e164C6Adf", status: "live", deposit_closed: true, launchSharePrice: 0.90, launchDate: "2026-04-13" },
      // 9 new fleet gemstones (DESIGN.md §4.1). Each `address` / `launchSharePrice`
      // populates as each vault deploys via scripts/deploy_vault.sh. The deploy
      // driver measures `convertToAssets(1e9)/1e6` after step 10 (first deposit)
      // and before any trading — that's the $1.00 reference for the life of the vault.
      // V3.6 reserves the Core activation fee in spot (counted in totalAssets), so
      // launchSharePrice = $1.00 for new vaults. V3-era Onyx had the fee consumed
      // by HL (launchSharePrice = 0.90). Same display formula: price/launchPrice.
      // ccAmber REOPENED to new deposits 2026-06-13 (JP) — the live deposit/zap
      // target during the transition while Lagoon vaults aren't deposit-ready.
      // The other 4 founding-fleet vaults stay deposit_closed. Flip back to
      // deposit_closed:true to re-close. Withdraw is unaffected either way.
      { ...GEMSTONES.amber,     address: "0x1b463561f264114F9D4db6FF9eE2771B33076B13", status: "live", launchSharePrice: 1.00, launchDate: "2026-04-22" },
      { ...GEMSTONES.moonstone, address: "0x015A70185a80D8C8c034e3d360E25A14c7FB8cF0", status: "live", deposit_closed: true, launchSharePrice: 1.00, launchDate: "2026-04-30" },
      { ...GEMSTONES.peridot,   address: null, status: "coming_soon", launchSharePrice: null },
      { ...GEMSTONES.ruby,      address: "0xb44169e66C898FF70029f9CF2fdB9685d7bC99c6", status: "live", deposit_closed: true, launchSharePrice: 1.00, launchDate: "2026-04-28" },
      // Diamond instance 1 — RETIRED 2026-05-21 (≈40% drawdown, contract
      // permanently impaired). Renders as "Coming Soon" to non-holders (zero
      // stigma — see docs/architecture/GEMSTONE_RETIREMENT_DESIGN.md). Keeps
      // its address so the remaining holder's client-side withdraw scan
      // (overview.js) can read balanceOf + withdrawalRequests against it.
      // The identity GEMSTONES.diamond is reusable: a future Diamond v2 adds
      // a fresh entry with key:"diamond", status:"live", and the new address.
      { ...GEMSTONES.diamond, key: "diamond_v1", address: "0x318cA1Ee088eFfc96fA67c1bb50c7893C3c4Feaa", status: "retired", launchSharePrice: 1.00, retired_at: "2026-05-21" },
      { ...GEMSTONES.sapphire,  address: null, status: "coming_soon", launchSharePrice: null },
      { ...GEMSTONES.emerald,   address: "0x1efAE1f600947cA5dc0E87AA18657f36c559A40b", status: "live", deposit_closed: true, launchSharePrice: 1.00, launchDate: "2026-04-30" },
      { ...GEMSTONES.pearl,     address: null, status: "coming_soon", launchSharePrice: null },
      { ...GEMSTONES.opal,      address: null, status: "coming_soon", launchSharePrice: null },
    ],
  },
  testnet: {
    key: "testnet",
    label: "Testnet",
    chainId: 998,
    chainHex: "0x3e6",
    chainName: "HyperEVM Testnet",
    rpcUrl: "https://rpc.hyperliquid-testnet.xyz/evm",
    explorer: "https://testnet.hyperscan.com",
    hlExplorer: "https://app.hyperliquid-testnet.xyz/explorer",
    nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
    usdcAddress: "0x2B3370eE501B4a559b57D449569354196457D8Ab",
    usdcDecimals: 6,
    shareDecimals: 9,  // see mainnet comment — same OZ ERC4626 + offset=3 math
    isProduction: false,
    badgeText: "TESTNET — fake money",
    badgeColor: "amber",
    // Testnet V3 vault is deployed (0xbf8ce451...) but NOT initialized (coreInitialized=false)
    // and HL testnet bridges are unreliable — see project_vault_testnet_e2e_findings.md.
    // Marked coming_soon until both are resolved.
    vaults: [
      { ...GEMSTONES.onyx,      address: "0xbf8ce45185cdc5922bda7700a69d2131e341de2a", status: "coming_soon" },
      // Amber deployed on testnet 2026-04-22 as Phase 1 proof. activated but
      // HL testnet bridge drops deposits so totalAssets stays $0 (not a bug;
      // see feedback_testnet_bridge_drops_deposits.md). Marked active for UI.
      { ...GEMSTONES.amber,     address: "0xD9688189a825275C1754F2B6ABeCCdfBA912E5B7", status: "testnet_active", launchSharePrice: null },
      { ...GEMSTONES.moonstone, address: null, status: "coming_soon" },
      { ...GEMSTONES.peridot,   address: null, status: "coming_soon" },
      { ...GEMSTONES.ruby,      address: null, status: "coming_soon" },
      { ...GEMSTONES.diamond,   address: null, status: "coming_soon" },
      { ...GEMSTONES.sapphire,  address: null, status: "coming_soon" },
      { ...GEMSTONES.emerald,   address: null, status: "coming_soon" },
      { ...GEMSTONES.pearl,     address: null, status: "coming_soon" },
      { ...GEMSTONES.opal,      address: null, status: "coming_soon" },
    ],
  },
};

// ── Active network selection (persisted in localStorage) ─────────────────
const STORAGE_KEY = "cv_network";
const DEFAULT_NETWORK = "mainnet";

export function getActiveNetworkKey() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && NETWORKS[stored]) return stored;
  return DEFAULT_NETWORK;
}

export function setActiveNetworkKey(key) {
  if (!NETWORKS[key]) throw new Error(`Unknown network: ${key}`);
  localStorage.setItem(STORAGE_KEY, key);
  // Network switch is heavy (different RPC, different contracts) — reload for clean state
  window.location.reload();
}

export function getActiveNetwork() {
  return NETWORKS[getActiveNetworkKey()];
}

// ── Resolved-at-load constants (backward-compat with existing imports) ───
// These read the active network ONCE at module evaluation. To switch
// networks at runtime, call setActiveNetworkKey() which reloads the page.
const _N = getActiveNetwork();
export const NETWORK = _N;
export const VAULT_CONFIGS = _N.vaults;
export const USDC_ADDRESS  = _N.usdcAddress;
export const USDC_DECIMALS = _N.usdcDecimals;
export const SHARE_DECIMALS = _N.shareDecimals;
export const CHAIN_ID  = _N.chainId;
export const RPC_URL   = _N.rpcUrl;
export const EXPLORER  = _N.explorer;
// HL Core trade explorer base URL. Use as: `${HL_EXPLORER}/address/${vc.address}`
// The vault contract address doubles as its HL Core user address; this URL
// shows live fills, positions, funding payments — all the trading activity
// that Hyperscan can't see because it's on a different ledger (HL Core perps,
// not HyperEVM). Differentiated for mainnet vs testnet via the NETWORK entry.
export const HL_EXPLORER = _N.hlExplorer;

// ── ABIs (CrystalVaultV3 — audit-hardened, OZ ERC4626 base) ──────────────
//
// Aligned to contracts/src/CrystalVaultV3.sol. Notable changes from V1/V2:
//   - managedAssets() REMOVED (audit fix H-2)
//   - withdrawalRequests stores (shares, costBasisSnapshot, totalSharesSnapshot,
//     requestedAt, snapshottedAssets) — V3.6 LOCKS the gross-asset payout at
//     request time via snapshottedAssets. Previously (V3.4/V3.5) the claim
//     was recomputed against live state at claim time; V3.6 freezes it so
//     post-request totalAssets drift doesn't move the user's payout.
//     FRONTEND CONSEQUENCE: ANY page that previews "claimable" MUST read
//     snapshottedAssets and use it directly when > 0 (V3.6 path). Falling
//     back to the (shares * totalAssets / totalSupply) recomputation is
//     ONLY correct for legacy pre-V3.6 pending requests where the snapshot
//     field is 0. Got bitten 2026-05-19 on Diamond: UI showed $10.26
//     claimable while contract was about to pay $24.02.
//   - totalPendingWithdrawals → renamed to totalPendingShares
//   - Deposit event renamed to DepositPerformed
//   - WithdrawalRequested event no longer carries assetsOwed/feePaid
//   - WithdrawalClaimed event now carries (grossAssets, fee, netAssets)
export const VAULT_ABI = [
  // Reads
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
  "function totalAssets() view returns (uint256)",
  "function convertToShares(uint256) view returns (uint256)",
  "function convertToAssets(uint256) view returns (uint256)",
  "function previewDeposit(uint256) view returns (uint256)",
  "function previewRedeem(uint256) view returns (uint256)",
  "function performanceFeeBps() view returns (uint256)",
  "function builderFeeRate() view returns (uint64)",
  "function userCostBasis(address) view returns (uint256)",
  "function withdrawalRequests(address) view returns (uint256 shares, uint256 costBasisSnapshot, uint256 totalSharesSnapshot, uint64 requestedAt, uint256 snapshottedAssets)",
  "function totalPendingShares() view returns (uint256)",
  "function coreInitialized() view returns (bool)",
  "function coreAccountActivated() view returns (bool)",
  "function paused() view returns (bool)",
  "function maxTVL() view returns (uint256)",
  "function minDeposit() view returns (uint256)",
  // Writes
  "function deposit(uint256 assets, address receiver) returns (uint256)",
  "function requestWithdraw(uint256 shares)",
  "function claimWithdraw()",
  // Events
  "event DepositPerformed(address indexed user, uint256 assets, uint256 shares)",
  "event WithdrawalRequested(address indexed user, uint256 shares, uint64 requestedAt)",
  "event WithdrawalClaimed(address indexed user, uint256 grossAssets, uint256 fee, uint256 netAssets)",
];

export const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
];
